import { useState, useCallback } from 'react';
import conversationService from '../services/conversationService';
import useNotification from './useNotification';
import { copyWithHtml, stripMarkdown } from '../utils/clipboardUtils';

/**
 * Custom hook for CRUD operations on sections - V1 (Optimized)
 *
 * Key difference from useSectionCRUD:
 * - Does NOT use editedContent state for typing (avoids re-renders on every keystroke)
 * - Uses handleSaveEditWithContent which accepts content directly
 * - handleStartEditV1 only sets editingSection, not editedContent
 *
 * This hook is designed to work with IsolatedEditSection component
 * which manages its own local state for typing performance.
 */
const useSectionCRUDV1 = (conversation, sections, setSections, setExpandedSections, contentRef, updateCurrentVersion = null, currentVersion = null, isLocalUpdateRef = null, onConversationUpdate = null, markLocalEdit = null) => {
  const [editingSection, setEditingSection] = useState(null);
  const [editedTitle, setEditedTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, section: null });

  const { showNotification } = useNotification();

  // Handler for starting edit mode - V1 version doesn't set editedContent
  // The content will be passed directly to handleSaveEditWithContent
  const handleStartEditV1 = useCallback((sectionId, title = '') => {
    setEditingSection(sectionId);
    setEditedTitle(title);
  }, []);

  // Handler for canceling edit
  const handleCancelEdit = useCallback(() => {
    setEditingSection(null);
    setEditedTitle('');
  }, []);

  // Handler for saving with content passed directly (for isolated edit components)
  // This avoids needing to call setEditedContent on every keystroke
  const handleSaveEditWithContent = useCallback(async (sectionId, content) => {
    console.log('🚀 useSectionCRUDV1.handleSaveEditWithContent called for:', sectionId);

    try {
      setIsSaving(true);

      // Find the section being edited
      const sectionIndex = sections.findIndex(s => s.id === sectionId);
      if (sectionIndex === -1) {
        console.log('❌ Section not found:', sectionId);
        return;
      }

      // Update the section content and title using the passed content directly
      const updatedSections = [...sections];
      const currentSection = updatedSections[sectionIndex];
      // Use editedTitle if provided, otherwise keep original title
      const newTitle = editedTitle || currentSection.title;
      updatedSections[sectionIndex] = {
        ...currentSection,
        title: newTitle,
        content: content,
        rawContent: content
      };

      // Recalculate isFirstInCategory flags
      let lastSeenCategory = null;
      updatedSections.forEach(section => {
        if (section.category && section.category !== lastSeenCategory) {
          section.isFirstInCategory = true;
          lastSeenCategory = section.category;
        } else {
          section.isFirstInCategory = false;
        }
      });

      // Prevent useParsedSections from re-fetching and overwriting the edit
      if (markLocalEdit) markLocalEdit();
      if (isLocalUpdateRef) isLocalUpdateRef.current = true;

      setSections(updatedSections);

      // Reconstruct the full content with updated section
      let updatedContent = '';
      updatedSections.forEach((section, idx) => {
        if (idx > 0) updatedContent += '\n\n';
        if (section.level) {
          const headerPrefix = '#'.repeat(section.level);
          updatedContent += `${headerPrefix} ${section.title}\n\n${section.content}`;
        } else if (section.category) {
          if (section.isFirstInCategory) {
            updatedContent += `# ${section.category}\n\n`;
          }
          updatedContent += `## ${section.title}\n\n${section.content}`;
        } else {
          updatedContent += `## ${section.title}\n\n${section.content}`;
        }
      });

      // Update content ref
      contentRef.current = updatedContent.trim();

      // Update the current version if updateCurrentVersion function is provided
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent.trim(),
          features: sections.some(s => s.title?.includes('F0')) ? updatedSections : currentVersion.features,
          stories: sections.some(s => s.id?.includes('US')) ? updatedSections : currentVersion.stories,
          roles: sections.some(s => s.id?.includes('UR')) ? updatedSections : currentVersion.roles,
          sections: updatedSections,
          parsedContent: updatedSections
        });
      }

      // Save sections to ParsedResponse — backend syncs markdown to conversation message
      let messageTimestamp = currentVersion?.timestamp;
      // Fallback: find timestamp from last assistant message in conversation
      if (!messageTimestamp && conversation?.messages?.length) {
        for (let i = conversation.messages.length - 1; i >= 0; i--) {
          if (conversation.messages[i].role === 'assistant') {
            messageTimestamp = conversation.messages[i].createdAt;
            break;
          }
        }
      }
      if (!messageTimestamp || !conversation._id) {
        console.error('❌ useSectionCRUDV1: Missing timestamp or conversation ID', {
          messageTimestamp, conversationId: conversation._id, currentVersionTimestamp: currentVersion?.timestamp
        });
        throw new Error('Missing timestamp or conversation ID for save');
      }

      console.log('💾 useSectionCRUDV1: Saving to ParsedResponse', {
        conversationId: conversation._id,
        messageTimestamp,
        sectionCount: updatedSections.length,
        source: currentVersion?.timestamp ? 'currentVersion' : 'fallback'
      });

      const result = await conversationService.updateParsedSections(
        conversation._id,
        messageTimestamp,
        updatedSections
      );
      if (result?.data?.markdown) {
        contentRef.current = result.data.markdown;
      }
      // Use sections from backend response (has re-extracted details)
      const savedSections = result?.data?.parsed_content?.sections || updatedSections;
      setSections(savedSections);

      // Notify parent component
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id, conversation.messages);
      }

      // Exit edit mode
      setEditingSection(null);
      setEditedTitle('');

      showNotification({
        message: "Section updated successfully",
        type: "success"
      });
    } catch (error) {
      console.error("Error saving section:", error);
      showNotification({
        message: "Failed to save changes",
        type: "error"
      });
    } finally {
      setIsSaving(false);
    }
  }, [conversation, sections, setSections, contentRef, updateCurrentVersion, currentVersion, isLocalUpdateRef, onConversationUpdate, showNotification, editedTitle]);

  // Handler for deleting section
  const handleDeleteSection = useCallback((section) => {
    setDeleteConfirmation({ open: true, section });
  }, []);

  // Handler for confirmed delete
  const handleDeleteSectionConfirmed = useCallback(async () => {
    const section = deleteConfirmation.section;
    setDeleteConfirmation({ open: false, section: null });

    if (!section) return;

    try {
      // Remove the section from the sections array
      const updatedSections = sections.filter(s => s.id !== section.id);

      // Recalculate isFirstInCategory flags
      let lastSeenCategory = null;
      updatedSections.forEach(s => {
        if (s.category && s.category !== lastSeenCategory) {
          s.isFirstInCategory = true;
          lastSeenCategory = s.category;
        } else {
          s.isFirstInCategory = false;
        }
      });

      // Set the flag to prevent reset from currentVersion if provided
      if (isLocalUpdateRef) {
        isLocalUpdateRef.current = true;
      }

      setSections(updatedSections);

      // Remove expanded state for this section
      setExpandedSections(prev => {
        const updated = { ...prev };
        delete updated[section.id];
        return updated;
      });

      // Reconstruct the full content without the deleted section
      let updatedContent = '';
      updatedSections.forEach((s, idx) => {
        if (idx > 0) updatedContent += '\n\n';
        if (s.level) {
          const headerPrefix = '#'.repeat(s.level);
          updatedContent += `${headerPrefix} ${s.title}\n\n${s.content}`;
        } else if (s.category) {
          if (s.isFirstInCategory) {
            updatedContent += `# ${s.category}\n\n`;
          }
          updatedContent += `## ${s.title}\n\n${s.content}`;
        } else {
          updatedContent += `## ${s.title}\n\n${s.content}`;
        }
      });

      // Update content ref
      contentRef.current = updatedContent.trim();

      // Update the current version if updateCurrentVersion function is provided
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent.trim(),
          features: sections.some(s => s.title?.includes('F0')) ? updatedSections : currentVersion.features,
          stories: sections.some(s => s.id?.includes('US')) ? updatedSections : currentVersion.stories,
          roles: sections.some(s => s.id?.includes('role-')) ? updatedSections : currentVersion.roles,
          sections: updatedSections,
          parsedContent: updatedSections
        });
      }

      // Save sections to ParsedResponse — backend syncs markdown to conversation message
      const messageTimestamp = currentVersion?.timestamp;
      if (!messageTimestamp || !conversation._id) {
        throw new Error('Missing timestamp or conversation ID for delete');
      }

      const result = await conversationService.updateParsedSections(
        conversation._id,
        messageTimestamp,
        updatedSections
      );
      if (result?.data?.markdown) {
        contentRef.current = result.data.markdown;
      }
      // Use sections from backend response (has re-extracted details)
      const savedSections = result?.data?.parsed_content?.sections || updatedSections;
      setSections(savedSections);

      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id, conversation.messages);
      }

      showNotification({
        message: `"${section.title}" deleted successfully`,
        type: "success"
      });
    } catch (error) {
      console.error("Error deleting section:", error);
      showNotification({
        message: `Failed to delete "${section.title}"`,
        type: "error"
      });
    }
  }, [conversation, sections, setSections, setExpandedSections, contentRef, updateCurrentVersion, currentVersion, isLocalUpdateRef, onConversationUpdate, deleteConfirmation.section, showNotification]);

  // Action handlers for sections
  const handleImproveSection = useCallback((section) => {
    showNotification({
      message: `Requesting improvements for ${section.title}`,
      type: "info"
    });
  }, [showNotification]);

  const handleExplainSection = useCallback((section) => {
    showNotification({
      message: `Requesting explanation for ${section.title}`,
      type: "info"
    });
  }, [showNotification]);

  const handleCopySection = useCallback(async (section) => {
    try {
      const markdown = `## ${section.title}\n\n${section.content || ''}`;
      const plainText = `${section.title}\n\n${stripMarkdown(section.content || '')}`;

      await copyWithHtml(markdown, plainText);
      showNotification({
        message: `"${section.title}" copied to clipboard`,
        type: "success"
      });
    } catch (error) {
      showNotification({
        message: `Failed to copy "${section.title}" to clipboard`,
        type: "error"
      });
    }
  }, [showNotification]);

  return {
    editingSection,
    editedTitle,
    setEditedTitle,
    isSaving,
    deleteConfirmation,
    setDeleteConfirmation,
    setEditingSection,
    setIsSaving,
    handleStartEditV1,
    handleCancelEdit,
    handleSaveEditWithContent,
    handleDeleteSection,
    handleDeleteSectionConfirmed,
    handleImproveSection,
    handleExplainSection,
    handleCopySection
  };
};

export default useSectionCRUDV1;
